import { types } from "../../actions/courier";
import { INITIAL_STATE } from "../initial-state";
import { formatApiError } from "../../utils/api-error";

const mutationFailure = (state, payload) => ({
  ...state,
  loading: false,
  apiError: formatApiError(payload),
});

const mutationStart = state => ({
  ...state,
  loading: true,
  apiError: null,
});

const mutationSuccess = state => ({
  ...state,
  loading: false,
  apiError: null,
});

const extractList = payload =>
  payload && Array.isArray(payload.results) ? payload.results : payload || [];

const extractCount = payload =>
  payload && typeof payload.count === "number"
    ? payload.count
    : extractList(payload).length;

export const courier = (state = INITIAL_STATE.courier, action) => {
  const { payload } = action;
  switch (action.type) {
    // Zones
    case types.GET_COURIER_ZONES_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_ZONES_SUCCESS:
      return {
        ...state,
        loading: false,
        zones: extractList(payload),
        zonesCount: extractCount(payload)
      };
    case types.GET_COURIER_ZONES_FAILURE:
      return { ...state, loading: false };

    // Centers
    case types.GET_COURIER_CENTERS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_CENTERS_SUCCESS:
      return {
        ...state,
        loading: false,
        centers: extractList(payload),
        centersCount: extractCount(payload)
      };
    case types.GET_COURIER_CENTERS_FAILURE:
      return { ...state, loading: false };

    // Couriers
    case types.GET_COURIERS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIERS_SUCCESS:
      return {
        ...state,
        loading: false,
        couriers: extractList(payload),
        couriersCount: extractCount(payload)
      };
    case types.GET_COURIERS_FAILURE:
      return { ...state, loading: false };

    // Rounds
    case types.GET_COURIER_ROUNDS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_ROUNDS_SUCCESS:
      return {
        ...state,
        loading: false,
        rounds: extractList(payload),
        roundsCount: extractCount(payload)
      };
    case types.GET_COURIER_ROUNDS_FAILURE:
      return { ...state, loading: false };

    // Round stops (scoped to the currently open round)
    case types.GET_COURIER_ROUND_STOPS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_ROUND_STOPS_SUCCESS:
      return { ...state, loading: false, roundStops: extractList(payload) };
    case types.GET_COURIER_ROUND_STOPS_FAILURE:
      return { ...state, loading: false };

    // Round schedules
    case types.GET_COURIER_ROUND_SCHEDULES_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_ROUND_SCHEDULES_SUCCESS:
      return {
        ...state,
        loading: false,
        roundSchedules: extractList(payload),
        roundSchedulesCount: extractCount(payload)
      };
    case types.GET_COURIER_ROUND_SCHEDULES_FAILURE:
      return { ...state, loading: false };

    // Shifts
    case types.GET_COURIER_SHIFTS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_SHIFTS_SUCCESS:
      return {
        ...state,
        loading: false,
        shifts: extractList(payload),
        shiftsCount: extractCount(payload)
      };
    case types.GET_COURIER_SHIFTS_FAILURE:
      return { ...state, loading: false };

    // Trips
    case types.GET_COURIER_TRIPS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_TRIPS_SUCCESS:
      return {
        ...state,
        loading: false,
        trips: extractList(payload),
        tripsCount: extractCount(payload)
      };
    case types.GET_COURIER_TRIPS_FAILURE:
      return { ...state, loading: false };

    // Trip tasks (scoped to the currently open trip)
    case types.GET_COURIER_TRIP_TASKS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_TRIP_TASKS_SUCCESS:
      return { ...state, loading: false, tripTasks: extractList(payload) };
    case types.GET_COURIER_TRIP_TASKS_FAILURE:
      return { ...state, loading: false };

    // Eligible couriers (for assignment)
    case types.GET_ELIGIBLE_COURIERS_REQUEST:
      return { ...state, loading: true };
    case types.GET_ELIGIBLE_COURIERS_SUCCESS:
      return { ...state, loading: false, eligibleCouriers: extractList(payload) };
    case types.GET_ELIGIBLE_COURIERS_FAILURE:
      return { ...state, loading: false };

    // Custody items (scoped to the currently open task)
    case types.GET_COURIER_CUSTODY_ITEMS_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_CUSTODY_ITEMS_SUCCESS:
      return { ...state, loading: false, custodyItems: extractList(payload) };
    case types.GET_COURIER_CUSTODY_ITEMS_FAILURE:
      return { ...state, loading: false };

    // Reports & live monitor
    case types.GET_COURIER_REPORTS_REQUEST:
    case types.GET_COURIER_REPORT_REQUEST:
    case types.GET_COURIER_LIVE_MONITOR_REQUEST:
      return { ...state, loading: true };
    case types.GET_COURIER_REPORTS_SUCCESS:
      return { ...state, loading: false, reportsList: extractList(payload) };
    case types.GET_COURIER_REPORT_SUCCESS:
      return { ...state, loading: false, report: payload || null };
    case types.GET_COURIER_LIVE_MONITOR_SUCCESS:
      return { ...state, loading: false, liveMonitor: payload || {} };
    case types.GET_COURIER_REPORTS_FAILURE:
    case types.GET_COURIER_REPORT_FAILURE:
    case types.GET_COURIER_LIVE_MONITOR_FAILURE:
      return { ...state, loading: false };

    // Create/update/delete: reflect loading; lists refetched by sagas
    case types.CREATE_COURIER_ZONE_REQUEST:
    case types.UPDATE_COURIER_ZONE_REQUEST:
    case types.DELETE_COURIER_ZONE_REQUEST:
    case types.REORDER_COURIER_ZONES_REQUEST:
    case types.CREATE_COURIER_CENTER_REQUEST:
    case types.UPDATE_COURIER_CENTER_REQUEST:
    case types.DELETE_COURIER_CENTER_REQUEST:
    case types.REORDER_COURIER_ZONE_CENTERS_REQUEST:
    case types.CREATE_COURIER_REQUEST:
    case types.UPDATE_COURIER_REQUEST:
    case types.CREATE_COURIER_ROUND_REQUEST:
    case types.UPDATE_COURIER_ROUND_REQUEST:
    case types.DELETE_COURIER_ROUND_REQUEST:
    case types.CREATE_COURIER_ROUND_STOP_REQUEST:
    case types.DELETE_COURIER_ROUND_STOP_REQUEST:
    case types.REORDER_COURIER_ROUND_STOPS_REQUEST:
    case types.CREATE_COURIER_ROUND_SCHEDULE_REQUEST:
    case types.DELETE_COURIER_ROUND_SCHEDULE_REQUEST:
    case types.CREATE_COURIER_SHIFT_REQUEST:
    case types.DELETE_COURIER_SHIFT_REQUEST:
    case types.GENERATE_COURIER_TRIPS_REQUEST:
    case types.ASSIGN_COURIER_TRIP_REQUEST:
    case types.CANCEL_COURIER_TRIP_REQUEST:
    case types.START_COURIER_TRIP_REQUEST:
    case types.DELIVER_COURIER_TRIP_REQUEST:
    case types.REACH_COURIER_TASK_REQUEST:
    case types.DISPATCH_COURIER_TASK_REQUEST:
    case types.OUTCOME_COURIER_TASK_REQUEST:
    case types.OVERRIDE_GPS_COURIER_TASK_REQUEST:
    case types.CREATE_COURIER_CUSTODY_ITEM_REQUEST:
    case types.RECONCILE_COURIER_CUSTODY_ITEM_REQUEST:
    case types.CREATE_COURIER_MANUAL_JOB_REQUEST:
    case types.OPTIMIZE_ROUND_STOPS_REQUEST:
    case types.AUTO_ASSIGN_COURIER_TRIP_REQUEST:
      return mutationStart(state);

    case types.CREATE_COURIER_ZONE_SUCCESS:
    case types.UPDATE_COURIER_ZONE_SUCCESS:
    case types.DELETE_COURIER_ZONE_SUCCESS:
    case types.REORDER_COURIER_ZONES_SUCCESS:
    case types.CREATE_COURIER_CENTER_SUCCESS:
    case types.UPDATE_COURIER_CENTER_SUCCESS:
    case types.DELETE_COURIER_CENTER_SUCCESS:
    case types.REORDER_COURIER_ZONE_CENTERS_SUCCESS:
    case types.CREATE_COURIER_SUCCESS:
    case types.UPDATE_COURIER_SUCCESS:
    case types.CREATE_COURIER_ROUND_SUCCESS:
    case types.UPDATE_COURIER_ROUND_SUCCESS:
    case types.DELETE_COURIER_ROUND_SUCCESS:
    case types.CREATE_COURIER_ROUND_STOP_SUCCESS:
    case types.DELETE_COURIER_ROUND_STOP_SUCCESS:
    case types.REORDER_COURIER_ROUND_STOPS_SUCCESS:
    case types.CREATE_COURIER_ROUND_SCHEDULE_SUCCESS:
    case types.DELETE_COURIER_ROUND_SCHEDULE_SUCCESS:
    case types.CREATE_COURIER_SHIFT_SUCCESS:
    case types.DELETE_COURIER_SHIFT_SUCCESS:
    case types.GENERATE_COURIER_TRIPS_SUCCESS:
    case types.ASSIGN_COURIER_TRIP_SUCCESS:
    case types.CANCEL_COURIER_TRIP_SUCCESS:
    case types.START_COURIER_TRIP_SUCCESS:
    case types.DELIVER_COURIER_TRIP_SUCCESS:
    case types.REACH_COURIER_TASK_SUCCESS:
    case types.DISPATCH_COURIER_TASK_SUCCESS:
    case types.OUTCOME_COURIER_TASK_SUCCESS:
    case types.OVERRIDE_GPS_COURIER_TASK_SUCCESS:
    case types.CREATE_COURIER_CUSTODY_ITEM_SUCCESS:
    case types.RECONCILE_COURIER_CUSTODY_ITEM_SUCCESS:
    case types.CREATE_COURIER_MANUAL_JOB_SUCCESS:
    case types.OPTIMIZE_ROUND_STOPS_SUCCESS:
    case types.AUTO_ASSIGN_COURIER_TRIP_SUCCESS:
      return mutationSuccess(state);

    case types.CREATE_COURIER_ZONE_FAILURE:
    case types.UPDATE_COURIER_ZONE_FAILURE:
    case types.DELETE_COURIER_ZONE_FAILURE:
    case types.REORDER_COURIER_ZONES_FAILURE:
    case types.CREATE_COURIER_CENTER_FAILURE:
    case types.UPDATE_COURIER_CENTER_FAILURE:
    case types.DELETE_COURIER_CENTER_FAILURE:
    case types.REORDER_COURIER_ZONE_CENTERS_FAILURE:
    case types.CREATE_COURIER_FAILURE:
    case types.UPDATE_COURIER_FAILURE:
    case types.CREATE_COURIER_ROUND_FAILURE:
    case types.UPDATE_COURIER_ROUND_FAILURE:
    case types.DELETE_COURIER_ROUND_FAILURE:
    case types.CREATE_COURIER_ROUND_STOP_FAILURE:
    case types.DELETE_COURIER_ROUND_STOP_FAILURE:
    case types.REORDER_COURIER_ROUND_STOPS_FAILURE:
    case types.CREATE_COURIER_ROUND_SCHEDULE_FAILURE:
    case types.DELETE_COURIER_ROUND_SCHEDULE_FAILURE:
    case types.CREATE_COURIER_SHIFT_FAILURE:
    case types.DELETE_COURIER_SHIFT_FAILURE:
    case types.GENERATE_COURIER_TRIPS_FAILURE:
    case types.ASSIGN_COURIER_TRIP_FAILURE:
    case types.CANCEL_COURIER_TRIP_FAILURE:
    case types.START_COURIER_TRIP_FAILURE:
    case types.DELIVER_COURIER_TRIP_FAILURE:
    case types.REACH_COURIER_TASK_FAILURE:
    case types.DISPATCH_COURIER_TASK_FAILURE:
    case types.OUTCOME_COURIER_TASK_FAILURE:
    case types.OVERRIDE_GPS_COURIER_TASK_FAILURE:
    case types.CREATE_COURIER_CUSTODY_ITEM_FAILURE:
    case types.RECONCILE_COURIER_CUSTODY_ITEM_FAILURE:
    case types.CREATE_COURIER_MANUAL_JOB_FAILURE:
    case types.OPTIMIZE_ROUND_STOPS_FAILURE:
    case types.AUTO_ASSIGN_COURIER_TRIP_FAILURE:
      return mutationFailure(state, payload);

    case types.RESET_COURIER_STATE:
      return { ...state, ...INITIAL_STATE.courier };

    default:
      return state;
  }
};
